import React, { useState, useCallback, useRef } from 'react';
import { Box, Typography, Button, CircularProgress, Alert } from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import CustomBox from '../../components/CustomBox/CustomBox';
import ReportTypeSelector from '../../components/Reports/ReportTypeSelector';
import FieldSelector from '../../components/Reports/FieldSelector';
import ReportFilters, { ReportFiltersState } from '../../components/Reports/ReportFilters';
import SavedReportConfigs from '../../components/Reports/SavedReportConfigs';
import { generateReport, getAvailableFields, validateReportFields, AvailableFieldsResponse } from '../../services/reportsApi';
import { useAlert } from '../../contexts/AlertContext';
import { useTranslation } from 'react-i18next';
import styles from './Reports.module.css';

export type ReportType = 'products' | 'clients' | 'documents' | 'stock' | 'suppliers' | 'users' | 'locations';

export interface ReportField {
  id: string;
  label: string;
  table: string;
  tableLabel: string;
  selected: boolean;
}

const Reports: React.FC = () => {
  const [reportType, setReportType] = useState<ReportType | null>(null);
  const [fields, setFields] = useState<ReportField[]>([]);
  const [filters, setFilters] = useState<ReportFiltersState>({});
  const [availableFieldsData, setAvailableFieldsData] = useState<AvailableFieldsResponse | null>(null);
  const [loadingFields, setLoadingFields] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [validating, setValidating] = useState(false);
  const { showAlert } = useAlert();
  const { t } = useTranslation();

  // Ref to track if we're loading from a saved config (to apply field selection after fetch)
  const pendingFieldSelectionRef = useRef<string[] | null>(null);

  const initializeFieldsFromData = useCallback((data: AvailableFieldsResponse, selectedFieldIds?: string[]) => {
    const allFields: ReportField[] = [];

    // Add main table fields
    data.main_fields.forEach(field => {
      const fieldId = `${data.main_type}.${field.id}`;
      allFields.push({
        id: fieldId,
        label: field.label,
        table: data.main_type,
        tableLabel: data.main_label,
        selected: selectedFieldIds
          ? selectedFieldIds.includes(fieldId)
          : ['id', 'name'].includes(field.id),
      });
    });

    // Add related table fields
    data.related_tables.forEach(relatedTable => {
      relatedTable.fields.forEach(field => {
        const fieldId = `${relatedTable.table}.${field.id}`;
        allFields.push({
          id: fieldId,
          label: field.label,
          table: relatedTable.table,
          tableLabel: relatedTable.label,
          selected: selectedFieldIds
            ? selectedFieldIds.includes(fieldId)
            : false,
        });
      });
    });

    // If we have selectedFieldIds, reorder so selected fields come first in the saved order
    if (selectedFieldIds) {
      const selectedOrder = new Map(selectedFieldIds.map((id, idx) => [id, idx]));
      allFields.sort((a, b) => {
        const aSelected = selectedOrder.has(a.id);
        const bSelected = selectedOrder.has(b.id);
        if (aSelected && bSelected) {
          return (selectedOrder.get(a.id) ?? 0) - (selectedOrder.get(b.id) ?? 0);
        }
        if (aSelected) return -1;
        if (bSelected) return 1;
        return 0;
      });
    }

    return allFields;
  }, []);

  const handleReportTypeChange = useCallback(async (type: ReportType) => {
    console.log('[Reports] handleReportTypeChange - Type:', type);
    setReportType(type);
    setFields([]);
    setError(null);
    setLoadingFields(true);

    try {
      console.log('[Reports] handleReportTypeChange - Fetching available fields...');
      const data = await getAvailableFields(type);
      console.log('[Reports] handleReportTypeChange - Received data:', data);
      setAvailableFieldsData(data);

      // Check if we have a pending field selection from loading a saved config
      const pendingSelection = pendingFieldSelectionRef.current;
      pendingFieldSelectionRef.current = null;

      const allFields = initializeFieldsFromData(data, pendingSelection || undefined);
      setFields(allFields);
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || t('reports.messages.fetchFieldsError');
      setError(errorMessage);
      showAlert('error', errorMessage);
    } finally {
      setLoadingFields(false);
    }
  }, [t, showAlert, initializeFieldsFromData]);

  /**
   * Callback when a saved config is loaded.
   * If the report type matches current, just update field selection.
   * If different, trigger a type change which will fetch new fields,
   * and apply selection once data arrives.
   */
  const handleLoadConfig = useCallback(async (
    configReportType: ReportType,
    selectedFieldIds: string[],
    configFilters: Record<string, any>
  ) => {
    if (configFilters && Object.keys(configFilters).length > 0) {
      setFilters(configFilters as ReportFiltersState);
    }
    if (configReportType === reportType && availableFieldsData) {
      // Same report type - just update field selections
      const updatedFields = initializeFieldsFromData(availableFieldsData, selectedFieldIds);
      setFields(updatedFields);
      setError(null);
      setValidationError(null);
    } else {
      // Different report type - need to fetch fields first
      pendingFieldSelectionRef.current = selectedFieldIds;
      handleReportTypeChange(configReportType);
    }
  }, [reportType, availableFieldsData, initializeFieldsFromData, handleReportTypeChange]);

  const handleFieldsChange = async (newFields: ReportField[]) => {
    setFields(newFields);
    setError(null);
    setValidationError(null);

    // Validate fields if report type is selected
    if (reportType) {
      const selectedFields = newFields.filter(f => f.selected);
      if (selectedFields.length > 0) {
        setValidating(true);
        try {
          const fieldIds = selectedFields.map(f => f.id);
          console.log('[Reports] handleFieldsChange - Validating fields:', fieldIds);
          const validation = await validateReportFields(reportType, fieldIds);
          console.log('[Reports] handleFieldsChange - Validation result:', validation);

          if (!validation.valid && validation.error) {
            setValidationError(validation.error);
          } else {
            setValidationError(null);
          }
        } catch (err: any) {
          console.error('[Reports] handleFieldsChange - Validation error:', err);
          // Don't show error for validation failures - just log it
        } finally {
          setValidating(false);
        }
      } else {
        setValidationError(null);
      }
    }
  };

  const handleGenerateReport = async () => {
    console.log('[Reports] handleGenerateReport - Starting...');

    if (!reportType) {
      console.warn('[Reports] handleGenerateReport - No report type selected');
      setError(t('reports.validation.noReportType'));
      return;
    }

    const selectedFields = fields.filter(f => f.selected);
    console.log('[Reports] handleGenerateReport - Selected fields:', selectedFields);

    if (selectedFields.length === 0) {
      console.warn('[Reports] handleGenerateReport - No fields selected');
      setError(t('reports.validation.noFieldsSelected'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Use full field IDs (table.field format)
      const fieldIds = selectedFields.map(f => f.id);
      console.log('[Reports] handleGenerateReport - Calling generateReport with:', {
        reportType,
        fieldIds,
        fieldCount: fieldIds.length
      });

      await generateReport(reportType, fieldIds, Object.keys(filters).length > 0 ? filters : undefined);

      console.log('[Reports] handleGenerateReport - Success!');
      showAlert('success', t('reports.messages.generated'));
    } catch (err: any) {
      console.error('[Reports] handleGenerateReport - Error caught:', err);
      console.error('[Reports] handleGenerateReport - Error details:', {
        message: err.message,
        response: err.response,
        status: err.response?.status,
        data: err.response?.data
      });

      const errorMessage = err.response?.data?.detail ||
        err.response?.data?.error ||
        err.message ||
        t('reports.messages.generateError');
      console.error('[Reports] handleGenerateReport - Error message:', errorMessage);
      setError(errorMessage);
      showAlert('error', errorMessage);
    } finally {
      setLoading(false);
      console.log('[Reports] handleGenerateReport - Finished');
    }
  };

  return (
    <Box>
      <CustomBox>
        <Typography variant="h4" className={styles.title}>
          {t('reports.title')}
        </Typography>

        <Box className={styles.content}>
          {/* Saved report configurations */}
          <SavedReportConfigs
            reportType={reportType}
            fields={fields}
            filters={filters}
            onLoadConfig={handleLoadConfig}
          />

          <ReportTypeSelector
            value={reportType}
            onChange={handleReportTypeChange}
          />

          {reportType && (
            <>
              <ReportFilters
                reportType={reportType}
                filters={filters}
                onChange={setFilters}
              />
              <FieldSelector
                reportType={reportType}
                fields={fields}
                availableFieldsData={availableFieldsData}
                onChange={handleFieldsChange}
                loading={loadingFields}
              />
            </>
          )}

          {validationError && (
            <Alert severity="warning" className={styles.errorAlert}>
              {validationError}
            </Alert>
          )}

          {error && (
            <Alert severity="error" className={styles.errorAlert}>
              {error}
            </Alert>
          )}

          <Box className={styles.actions}>
            <Button
              variant="contained"
              startIcon={loading ? <CircularProgress size={20} /> : <DownloadIcon />}
              onClick={handleGenerateReport}
              disabled={loading || validating || !reportType || fields.filter(f => f.selected).length === 0 || !!validationError}
              className={styles.generateButton}
            >
              {loading ? t('reports.generating') : validating ? t('reports.validating') : t('reports.generateButton')}
            </Button>
          </Box>
        </Box>
      </CustomBox>
    </Box>
  );
};

export default Reports;

